import { useId, useState } from 'react';
import type { FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { WorkspaceSecretView } from '@/lib/api/generated/model';

import { isSecretError } from '../errors';
import {
  MAX_DESCRIPTION_CHARS,
  descriptionLength,
  validateDescription,
} from '../validation';

/** Past this, the count tells someone something they cannot see by looking. */
const COUNTER_THRESHOLD = MAX_DESCRIPTION_CHARS * 0.8;

/**
 * Edit one entry's description.
 *
 * Its own surface, deliberately not a field on the replace-value form. Those are
 * separate acts with different consequences — this one is a correction, that one
 * is irreversible for a secret — and a single form doing both invites the second
 * by accident. More concretely: the replace-value form cannot be submitted
 * without a value, and a secret's value cannot be read, so routing a typo fix
 * through it would demand the one thing the person does not have.
 *
 * There is no value field here at all, and the request carries none. That is
 * what makes a secret's note correctable by someone who cannot read the secret.
 *
 * Clearing is a first-class outcome: an emptied field sends `null`, and the
 * entry afterwards is indistinguishable from one that never had a description.
 */
export function SecretDescriptionSheet({
  entry,
  open,
  isPending,
  error,
  onSubmit,
  onClearError,
  onCancel,
}: {
  entry: WorkspaceSecretView;
  open: boolean;
  isPending: boolean;
  error: unknown;
  onSubmit: (description: string | null) => void;
  onClearError: () => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState(entry.description ?? '');
  const [submitted, setSubmitted] = useState(false);

  const fieldId = useId();
  const errorId = useId();
  const noteId = useId();

  const serverError = isSecretError(error) ? error : undefined;
  const localError = validateDescription(description);

  // Routed by the field the refusal belongs to, not by "a refusal happened".
  // Rendering a form-level error under the input — "That entry no longer
  // exists", say, after someone deleted it in another tab — states that the
  // text they typed is the problem, and sends them to edit prose that was
  // never wrong. `SecretFormSheet` splits these the same way, for the same
  // reason.
  const fieldMessage =
    serverError?.field === 'description'
      ? serverError.message
      : submitted
        ? localError
        : null;
  const formMessage =
    serverError && serverError.field !== 'description'
      ? serverError.message
      : null;

  const length = descriptionLength(description);
  const isClearing = description.trim() === '' && entry.description !== null;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (isPending) return;
    // Trimmed to `null` here as well as on the service, so "clear it" is one
    // outcome rather than two that happen to look alike.
    onSubmit(description.trim() === '' ? null : description.trim());
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (isPending) return;
        if (!next) onCancel();
      }}
    >
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Describe {entry.key}</SheetTitle>
          <SheetDescription>
            {/* FR-016. Both halves matter: people will otherwise assume a field
                on a secrets page is as private as the secrets, and they will
                not guess that an agent reads it. */}
            Anyone with access to this workspace can read this, and every agent
            that runs a step here receives it alongside the entry&rsquo;s name.
            Do not put the value itself here.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <SheetBody className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={fieldId}>Description</Label>
              {/* An Input, not a Textarea: a description is one line by rule,
                  and a multi-line control would invite exactly the line breaks
                  the service refuses. */}
              <Input
                id={fieldId}
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value);
                  if (serverError) onClearError();
                }}
                disabled={isPending}
                placeholder="Live key for the billing account, rotates quarterly"
                autoFocus
                aria-invalid={fieldMessage ? true : undefined}
                aria-describedby={fieldMessage ? errorId : noteId}
              />
              {fieldMessage ? (
                <p id={errorId} className="text-destructive text-xs">
                  {fieldMessage}
                </p>
              ) : (
                <p id={noteId} className="text-muted-foreground text-xs">
                  {isClearing
                    ? 'Saving now removes the description.'
                    : 'One line. It helps whoever finds this entry later, including an agent.'}
                </p>
              )}
              {length > COUNTER_THRESHOLD ? (
                <p className="text-muted-foreground text-xs tabular-nums">
                  {length} of {MAX_DESCRIPTION_CHARS} characters
                </p>
              ) : null}
            </div>

            {/* Form level: about the entry or the service, not about the text
                in the box above. `role="alert"` because it usually arrives
                after a submit the person expected to succeed. */}
            {formMessage ? (
              <p role="alert" className="text-destructive text-sm">
                {formMessage}
              </p>
            ) : null}
          </SheetBody>

          <SheetFooter>
            <SheetClose
              render={<Button type="button" variant="outline" />}
              disabled={isPending}
            >
              Cancel
            </SheetClose>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : isClearing ? 'Remove' : 'Save'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
